// Employee leave drawer — both balances (congés + récup), balance
// sparkline, unified transaction journal and récup history (proto
// CgDrawer). Opens from every table/calendar row on the two pages.
import { useId, useState } from 'react';
import {
  useLeaveChart, useLeaveDetail, useLeaveTransactions,
  type EmployeeClaimDTO, type EmployeeLeaveRequestDTO, type LeaveTxDTO, type LeaveType,
} from '../../api/hooks.js';
import { ENTITIES } from '@pointage/shared';
import { cgAvColor, cgFmtLong, cgFmtRange, cgInitials, cgNum, cgSigned } from './common.js';

const KIND_LABEL: Record<string, string> = {
  INITIAL_STOCK: 'Stock initial',
  MONTHLY_ACCRUAL: 'Acquisition mensuelle',
  MANUAL_ADDITION: 'Ajout manuel',
  CONSUMPTION_DIRECT: 'Consommation',
  CONSUMPTION_VIA_REQUEST: 'Demande débitée',
};

// Étapes du cycle d'une demande, côté lecture.
const STAGE_META: Record<string, { label: string; cls: string }> = {
  PROPOSED_BY_LAMIS: { label: 'En attente', cls: 'warn' },
  APPROVED: { label: 'Approuvée', cls: 'ok' },
  DEBITED: { label: 'Approuvée · débitée', cls: 'ok' },
  REJECTED: { label: 'Refusée', cls: 'bad' },
};
const CLAIM_META: Record<string, { label: string; cls: string }> = {
  PENDING: { label: 'En attente', cls: 'warn' },
  APPROVED: { label: 'Créditée', cls: 'ok' },
  REJECTED: { label: 'Refusée', cls: 'bad' },
};
const kindClass = (tx: LeaveTxDTO) =>
  tx.kind === 'MONTHLY_ACCRUAL' ? 'accrual'
    : tx.kind === 'INITIAL_STOCK' ? 'stock'
      : tx.type === 'RECUP' ? 'recup'
        : 'debit';

function Spark({ events, color }: { events: Array<{ date: string; balance: number }>; color: string }) {
  const gid = useId();
  const w = 360, h = 132, pad = 8;
  if (events.length === 0) return <div className="cg-empty sm">Pas encore de mouvements cette année.</div>;
  const vals = events.map((e) => e.balance);
  const max = Math.max(...vals, 1);
  const min = Math.min(...vals, 0);
  const x = (i: number) => events.length === 1 ? w / 2 : pad + (i * (w - pad * 2)) / (events.length - 1);
  const y = (v: number) => h - pad - ((v - min) / (max - min || 1)) * (h - pad * 2);
  const line = events.map((e, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(e.balance).toFixed(1)}`).join(' ');
  const area = `${line} L${x(events.length - 1)},${h - pad} L${x(0)},${h - pad} Z`;
  const last = events.length - 1;
  return (
    <svg className="cg-spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity=".26" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      {[0, 0.5, 1].map((f) => (
        <line key={f} x1="0" x2={w} y1={pad + f * (h - pad * 2)} y2={pad + f * (h - pad * 2)} stroke="var(--line)" strokeWidth="1" />
      ))}
      <path d={area} fill={`url(#${gid})`} className="cg-spark-area" />
      <path d={line} fill="none" stroke={color} strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round" className="cg-spark-line" />
      <circle cx={x(last)} cy={y(events[last]!.balance)} r="4.5" fill={color} stroke="var(--card)" strokeWidth="2" className="cg-spark-dot" />
    </svg>
  );
}

// Une demande: période, statut, et — le point du brief — d'où elle
// vient. Une demande déposée depuis l'Espace employé porte son badge.
function RequestRow({ r }: { r: EmployeeLeaveRequestDTO }) {
  const st = STAGE_META[r.stage] ?? { label: r.stage, cls: '' };
  return (
    <div className="cg-up">
      <span className="cg-up-bar" style={{ background: r.type === 'RECUP' ? 'var(--info)' : 'var(--shf)' }} />
      <div className="cg-up-m">
        <span className="cg-up-r">
          {cgFmtRange(r.startDate, r.endDate)}
          {r.origin === 'espace' && <span className="cg-srcchip">espace employé</span>}
        </span>
        <span className="cg-up-t">
          {r.type === 'RECUP' ? 'Récupération' : 'Congé annuel'} · {cgNum(r.daysCount)} j
          {r.proposedByName ? ` · proposé par ${r.proposedByName}` : ''}
          {r.decidedByName ? ` · décidé par ${r.decidedByName}` : ''}
        </span>
        {r.reason && <span className="cg-up-t quote">« {r.reason} »</span>}
        {r.rejectionReason && <span className="cg-up-t quote bad">Motif du refus : {r.rejectionReason}</span>}
      </div>
      <span className={'cg-stagechip ' + st.cls}>{st.label}</span>
    </div>
  );
}

function ClaimRow({ c }: { c: EmployeeClaimDTO }) {
  const st = CLAIM_META[c.stage] ?? { label: c.stage, cls: '' };
  return (
    <div className="cg-up">
      <span className="cg-up-bar" style={{ background: 'var(--info)' }} />
      <div className="cg-up-m">
        <span className="cg-up-r">{cgFmtLong(c.date)}{c.amount === 0.5 ? ' · demi-journée' : ''}</span>
        <span className="cg-up-t">{c.motif ?? 'Journée travaillée déclarée'}</span>
        {c.rejectionReason && <span className="cg-up-t quote bad">Motif du refus : {c.rejectionReason}</span>}
      </div>
      <span className={'cg-stagechip ' + st.cls}>{st.label}</span>
    </div>
  );
}

export function EmployeeDrawer({ employeeId, page, onClose, onNew, onCredit }: {
  employeeId: string;
  page: 'conges' | 'recup';
  onClose: () => void;
  onNew: (empId: string) => void;
  onCredit: (empId: string) => void;
}) {
  const [tab, setTab] = useState(page === 'recup' ? 'recup' : 'resume');
  const chartType: LeaveType = page === 'recup' ? 'RECUP' : 'CONGE';
  const conge = useLeaveDetail(employeeId, 'CONGE');
  const recup = useLeaveDetail(employeeId, 'RECUP');
  const chart = useLeaveChart(employeeId, chartType, new Date().getFullYear());
  const congeTx = useLeaveTransactions({ type: 'CONGE', employeeId });
  const recupTx = useLeaveTransactions({ type: 'RECUP', employeeId });

  const d = conge.data;
  const emp = d?.employee ?? null;
  const cb = d?.balance ?? null;
  const rb = recup.data?.balance ?? null;
  const pending = d?.pendingRequests ?? [];
  const pendingDays = pending.reduce((a, p) => a + p.daysCount, 0);
  const journal = [...(congeTx.data ?? []), ...(recupTx.data ?? [])]
    .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
  // Récup: ce qui a été CRÉDITÉ d'un côté, ce qui a été PRIS de l'autre —
  // « stock récup » sans le détail des jours posés ne dit qu'une moitié.
  const recupCredits = (recupTx.data ?? []).filter((t) => t.amount > 0);
  const recupTaken = (recupTx.data ?? []).filter((t) => t.amount < 0);
  const requests = d?.requests ?? [];
  const claims = d?.claims ?? [];
  const onLeaveNow = d?.onLeaveNow ?? null;
  const nEspace = requests.filter((r) => r.origin === 'espace').length + claims.length;
  // Detail DTO only carries entityShort — resolve the brand color from it.
  const entColor = ENTITIES.find((e) => e.short === emp?.entityShort)?.color ?? 'var(--ink3)';
  const ac = cgAvColor(employeeId);

  return (
    <div className="cg-veil" onClick={onClose}>
      <aside className="cg-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="cg-dr-top" style={{ '--ac': ac } as React.CSSProperties}>
          <span className="cg-dr-av" style={{ background: ac }}>{emp ? cgInitials(emp.name) : '··'}</span>
          <div className="cg-dr-id">
            <div className="cg-dr-name">{emp?.name ?? '…'}</div>
            <div className="cg-dr-meta">
              {emp && <span className="cg-entchip" style={{ '--ec': entColor } as React.CSSProperties}><i />{emp.entityShort}</span>}
              <span>{emp?.role ?? ''}</span>
            </div>
          </div>
          <button className="sb-dr-x" onClick={onClose} aria-label="Fermer">✕</button>
        </div>
        <div className="cg-dr-body">
          <div className="cg-dr-bals">
            <div className="cg-bal">
              <span className="cg-bal-l">Solde congés</span>
              <span className="cg-bal-v gm-mono">{cgNum(cb?.current ?? 0)}<i>j</i></span>
              <span className="cg-bal-s">
                {cgNum(cb?.initialStock ?? 0)} stock + {cgNum((cb?.accrued ?? 0) + (cb?.manualAdded ?? 0))} acquis − {cgNum(cb?.consumed ?? 0)} pris
              </span>
            </div>
            <div className="cg-bal recup">
              <span className="cg-bal-l">Stock récup</span>
              <span className="cg-bal-v gm-mono">{cgNum(rb?.current ?? 0)}<i>j</i></span>
              <span className="cg-bal-s">
                {cgNum((rb?.initialStock ?? 0) + (rb?.manualAdded ?? 0))} j crédités − {cgNum(rb?.consumed ?? 0)} j pris
              </span>
            </div>
          </div>
          {onLeaveNow && (
            <div className="cg-dr-now" data-testid="cg-dr-now">
              🌴 <b>Absent{onLeaveNow.type === 'RECUP' ? ' en récup' : ' en congé'} en ce moment</b>
              {onLeaveNow.until
                ? ` · du ${cgFmtLong(onLeaveNow.from)} au ${cgFmtLong(onLeaveNow.until)}`
                : ` · journée du ${cgFmtLong(onLeaveNow.from)}`}
            </div>
          )}
          {pendingDays > 0 && (
            <div className="cg-dr-warn">
              ⏳ {pending.length} demande{pending.length > 1 ? 's' : ''} en attente · solde projeté{' '}
              <b className="gm-mono">{cgNum((cb?.current ?? 0) - pendingDays)} j</b>
            </div>
          )}
          <div className="cg-dr-tabs">
            {([['resume', 'Résumé'], ['demandes', 'Demandes'], ['journal', 'Journal'], ['recup', 'Récup']] as const).map(([k, l]) => (
              <button key={k} className={'cg-dr-tab' + (tab === k ? ' on' : '')} onClick={() => setTab(k)} data-testid={`cg-dr-tab-${k}`}>
                {l}
                {k === 'demandes' && nEspace > 0 && <span className="cg-dr-tabn">{nEspace}</span>}
              </button>
            ))}
          </div>

          {tab === 'resume' && (
            <div className="view-enter">
              <div className="cg-dr-sec">Évolution du solde {chartType === 'RECUP' ? 'récup' : 'congés'} · {new Date().getFullYear()}</div>
              <div className="cg-chartwrap">
                <Spark events={chart.data?.events ?? []} color="var(--shf)" />
              </div>
              {pending.length > 0 && (
                <>
                  <div className="cg-dr-sec">Demandes en attente</div>
                  <div className="cg-uplist">
                    {pending.map((p) => (
                      <div key={p.id} className="cg-up">
                        <span className="cg-up-bar" style={{ background: 'var(--warn)' }} />
                        <div className="cg-up-m">
                          <span className="cg-up-r">{cgFmtRange(p.startDate, p.endDate)}</span>
                          <span className="cg-up-t">Congé annuel · {cgNum(p.daysCount)} j · en attente</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
              <div className="cg-dr-sec">Cette année</div>
              <div className="cg-minigrid">
                <div className="cg-mini"><b className="gm-mono">{cgNum(cb?.consumed ?? 0)}</b><span>Congés pris</span></div>
                <div className="cg-mini"><b className="gm-mono">{cgNum(rb?.consumed ?? 0)}</b><span>Récup prise</span></div>
                <div className="cg-mini"><b className="gm-mono">{cgNum((cb?.accrued ?? 0))}</b><span>Acquis</span></div>
                <div className="cg-mini"><b className="gm-mono">{cgNum((rb?.initialStock ?? 0) + (rb?.manualAdded ?? 0))}</b><span>Récup créditée</span></div>
              </div>
            </div>
          )}

          {/* Tout ce que la personne a demandé — depuis son Espace employé
              ou saisi par l'équipe — et la réponse qui lui a été faite. */}
          {tab === 'demandes' && (
            <div className="view-enter" data-testid="cg-dr-demandes">
              <div className="cg-dr-sec">Demandes de congé / récup<span className="cg-dr-secn">{requests.length}</span></div>
              {requests.length === 0 && <div className="cg-empty sm">Aucune demande enregistrée.</div>}
              <div className="cg-uplist">
                {requests.map((r) => <RequestRow key={r.id} r={r} />)}
              </div>
              <div className="cg-dr-sec">Journées travaillées déclarées<span className="cg-dr-secn">{claims.length}</span></div>
              {claims.length === 0 && <div className="cg-empty sm">Aucune déclaration.</div>}
              <div className="cg-uplist">
                {claims.map((c) => <ClaimRow key={c.id} c={c} />)}
              </div>
            </div>
          )}

          {tab === 'journal' && (
            <div className="cg-tl view-enter">
              {journal.map((tx) => (
                <div key={tx.id} className={'cg-tlrow ' + kindClass(tx)}>
                  <span className="cg-tl-date gm-mono">{tx.date}</span>
                  <span className="cg-tl-node" />
                  <div className="cg-tl-m">
                    <span className="cg-tl-lbl">{KIND_LABEL[tx.kind] ?? tx.kind}{tx.type === 'RECUP' ? ' · récup' : ''}</span>
                    <span className="cg-tl-sub">{tx.source}{tx.reason ? ` — ${tx.reason}` : ''}</span>
                  </div>
                  <span className={'cg-tl-v gm-mono ' + (tx.amount > 0 ? 'up' : tx.amount < 0 ? 'down' : 'zero')}>
                    {tx.amount === 0 ? '—' : `${cgSigned(tx.amount)} j`}
                  </span>
                </div>
              ))}
              {journal.length === 0 && <div className="cg-empty sm" style={{ marginTop: 12 }}>Aucun mouvement.</div>}
            </div>
          )}

          {tab === 'recup' && (
            <div className="view-enter">
              <div className="cg-dr-recapbar">
                <span><b className="gm-mono">{cgNum((rb?.initialStock ?? 0) + (rb?.manualAdded ?? 0))}</b> crédités</span>
                <span><b className="gm-mono">{cgNum(rb?.consumed ?? 0)}</b> pris</span>
                <span className="strong"><b className="gm-mono">{cgNum(rb?.current ?? 0)}</b> restants</span>
              </div>
              <div className="cg-dr-sec">Journées travaillées créditées<span className="cg-dr-secn">{recupCredits.length}</span></div>
              {recupCredits.length === 0 && <div className="cg-empty sm">Aucune journée créditée.</div>}
              <div className="cg-uplist">
                {recupCredits.map((t) => (
                  <div key={t.id} className="cg-up">
                    <span className="cg-up-bar" style={{ background: 'var(--info)' }} />
                    <div className="cg-up-m">
                      <span className="cg-up-r">{cgFmtLong(t.date)}{t.amount === 0.5 ? ' · demi-journée' : ''}</span>
                      <span className="cg-up-t">{t.reason ?? t.source}</span>
                    </div>
                    <span className="cg-up-v gm-mono">+{cgNum(t.amount)} j</span>
                  </div>
                ))}
              </div>
              {/* Le pendant du crédit: quand la récup a-t-elle été prise ? */}
              <div className="cg-dr-sec">Récup prise<span className="cg-dr-secn">{recupTaken.length}</span></div>
              {recupTaken.length === 0 && <div className="cg-empty sm">Aucune récup posée.</div>}
              <div className="cg-uplist">
                {recupTaken.map((t) => (
                  <div key={t.id} className="cg-up">
                    <span className="cg-up-bar" style={{ background: 'var(--warn)' }} />
                    <div className="cg-up-m">
                      <span className="cg-up-r">{cgFmtLong(t.date)}{t.amount === -0.5 ? ' · demi-journée' : ''}</span>
                      <span className="cg-up-t">{t.reason ?? t.source}</span>
                    </div>
                    <span className="cg-up-v gm-mono down">{cgNum(t.amount)} j</span>
                  </div>
                ))}
              </div>
              <button className="cg-ghost full" onClick={() => onCredit(employeeId)}>+ Créditer une journée travaillée</button>
            </div>
          )}
        </div>
        <div className="cg-dr-foot">
          <button className="gm-attn-go" onClick={() => onNew(employeeId)}>
            {page === 'recup' ? '↧ Poser une récup' : '+ Saisir un congé'}
          </button>
          <a className="cg-ghost" href={`/api/employees/${employeeId}/attestation`} target="_blank" rel="noreferrer">⇪ Attestation</a>
        </div>
      </aside>
    </div>
  );
}
